import { useCallback, useState } from 'react';
import type {
  TemplateComposerDraftOptions,
  TemplateComposerValues,
} from '../types/templates/template-composer.types';
import { templateDraftKey } from '../utils/template-composer.util';

/**
 * Keeps local drafts isolated across senders, recipients, variants and schema changes.
 * @param options - Current sender/recipient scope and refreshed provider definitions.
 * @returns Scoped selection and field drafts, with actions to edit drafts or reset selection.
 * @example
 * const draft = useTemplateComposerDraft({ scope, templates });
 * draft.selectTemplate(template.id);
 * draft.updateValue('body:1', 'María');
 */
export const useTemplateComposerDraft = ({
  scope,
  templates,
}: TemplateComposerDraftOptions) => {
  const [selection, setSelection] = useState<
    Record<string, string | undefined>
  >({});
  const [drafts, setDrafts] = useState<Record<string, TemplateComposerValues>>(
    {}
  );
  const selectedTemplate = templates.find(
    (template) => template.id === selection[scope]
  );
  const draftKey = JSON.stringify([
    scope,
    selectedTemplate ? templateDraftKey(selectedTemplate) : null,
  ]);
  const values = drafts[draftKey] ?? {};
  const selectTemplate = (id?: string) =>
    setSelection((current) => ({ ...current, [scope]: id }));
  const resetSelection = useCallback(() => setSelection({}), []);
  const updateValue = (key: string, value: string) =>
    setDrafts((current) => ({
      ...current,
      [draftKey]: { ...current[draftKey], [key]: value },
    }));
  const clearSubmittedDraft = () => {
    setDrafts((current) => {
      const next = { ...current };
      delete next[draftKey];
      return next;
    });
    selectTemplate(undefined);
  };
  return {
    selectedTemplate,
    values,
    selectTemplate,
    resetSelection,
    updateValue,
    clearSubmittedDraft,
    draftKey,
  };
};
